import { ReactNode, useCallback, useEffect, useState } from "react";
import { ApiError, setUnauthorizedHandler } from "@/api/client";
import { AuthContext, type User } from "./AuthContext";
import { fetchCurrentUser, logout as logoutRequest } from "./api";

/**
 * Cookie-based auth provider.
 *
 * On mount, asks the backend "who am I?" via GET /api/user/info. The
 * cookies are httpOnly — JS never sees tokens. A 401 here means the
 * user is logged out (apiRequest has already tried a silent refresh).
 *
 * Refresh-on-expiry is handled transparently by apiRequest itself:
 * any 401 on a protected route triggers POST /api/auth/refresh and
 * retries the original request. Only persistent 401s bubble up and
 * clear the user here.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const clearUser = useCallback(() => setUser(null), []);

  const logout = useCallback(async () => {
    try {
      const { logout_url } = await logoutRequest();
      clearUser();
      if (logout_url) {
        window.location.href = logout_url;
      }
    } catch {
      // Logout is best-effort; clear local state regardless.
      clearUser();
    }
  }, [clearUser]);

  useEffect(() => {
    // Wire the global 401 handler so apiRequest can ping us when a
    // background request loses the session.
    setUnauthorizedHandler(clearUser);
    return () => setUnauthorizedHandler(null);
  }, [clearUser]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fetched = await fetchCurrentUser();
        if (!cancelled) setUser(fetched);
      } catch (err) {
        if (!cancelled) {
          setUser(null);
          if (!(err instanceof ApiError) || err.status !== 401) {
            console.error("Failed to load user info:", err);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        setUser,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
